import { SHOP_FREE_ROLES, type Role, type UserListEntry } from "../api/types";

/**
 * G-102 — who may do what to a colleague on the Team page.
 *
 * These eight booleans were computed inline inside the table's row map
 * (`UsersPage.tsx`, before this change). The phone layout needs the identical
 * set for its card and action sheet, so they live here and both widths read
 * one definition rather than two that can drift — the same move G-95 made for
 * `deliveryTotals`. An auth-adjacent rule is the last place to keep a second
 * copy: G-30 found a client-side VAT calculation that had already drifted from
 * the server's.
 *
 * The rules are MOVED, not revised. Two pre-existing gaps against the server
 * are therefore preserved deliberately, and are not this order's to close:
 *
 *  - `assertCanEditTeamMember` (users.service.ts:477) also refuses a manager
 *    editing THEMSELVES, and requires the manager to share a shop with the
 *    target. The client checks neither for contact and contract, because the
 *    list row does not carry the viewer's own shop. A manager can therefore be
 *    shown a Contact button that the server answers with 403. Showing one
 *    button too many is a poor prompt, not a hole: the server decides.
 *  - `canSetLanguage` DOES mirror the server's self-edit rule, because G-85 S6
 *    wrote it against that function. A manager sets their own language in
 *    Profile, so the audit row records self-service honestly.
 */
export interface TeamViewer {
  /** The signed-in user's id; undefined before the session resolves. */
  id?: string;
  /** Their role, or null when there is no session. */
  role: Role | null;
}

export interface TeamMemberPermissions {
  canEditContact: boolean;
  canUploadContract: boolean;
  /** The "Pay rates" action — hourly rate and sales commission. */
  canEditPay: boolean;
  /**
   * The "Activity" action — what this person has done.
   *
   * G-105 opened this to managers (owner, 2026-09-22: *"managers should be
   * able to see it too"*). Same shape as `canSetLanguage`: a manager may ask
   * about an active florist, and about themselves, but nobody else. The server
   * narrows it further than this can — floor rows from the manager's own shop
   * only, and no security stream at all (OD-25) — because a button is a prompt
   * and the rows are the actual boundary.
   */
  canViewActivity: boolean;
  /**
   * The "Send reset email" action — email this person a link to choose a new
   * CRM password (G-104, reworked by G-136: nobody else chooses it any more).
   *
   * The same reach as `canEditContact` (owner anyone, manager an active
   * florist) minus yourself and minus owners, and only for an account in use
   * — someone still awaiting setup needs their invitation, not a reset.
   */
  canResetPassword: boolean;
  /**
   * G-136 — the "Invitation" action: resend or cancel it. Owner-only, like
   * creating the person; shown only while they have not set a password.
   */
  canManageInvite: boolean;
  /**
   * G-136 security review — may the email be changed from "Contact"? Moving
   * a sign-in address is as strong as resetting a password (a confirmed new
   * address can use "Forgot password?"), so never your own (Profile asks for
   * the current password) and never an owner's or admin's account in use
   * (OD-197, OD-216). Mirrors
   * `assertMayChangeEmailFromTeam` on the server, which decides.
   */
  canChangeEmail: boolean;
  canSetLanguage: boolean;
  canSetPin: boolean;
  canReassign: boolean;
  canDeactivate: boolean;
}

export function teamMemberPermissions(
  target: UserListEntry,
  viewer: TeamViewer,
): TeamMemberPermissions {
  const is = (...roles: Role[]) =>
    viewer.role !== null && roles.includes(viewer.role);

  // Owner/Admin can set a PIN for any active user (including other Owners).
  // Managers can set PINs only for non-Owner/non-Admin users — and never for
  // housekeeping: that PIN is owner-only because the OD-24 matrix is
  // assignment-based and cleaners have none (G-103, OD-69).
  // Couriers never get a PIN — no shop-floor access (server-enforced too).
  // G-136 (OD-195): no PIN before the person has accepted their invitation —
  // the server refuses it too.
  const accountInUse = target.accountStatus === "ACTIVE";

  const canSetPin =
    target.isActive &&
    accountInUse &&
    target.role !== "COURIER" &&
    (is("OWNER", "ADMIN") ||
      (is("MANAGER") &&
        target.role !== "OWNER" &&
        target.role !== "ADMIN" &&
        target.role !== "HOUSEKEEPING"));

  /*
   * G-85 S6 — who may set somebody else's language. Mirrors
   * `assertCanEditTeamMember` on the server exactly: an owner may set
   * anyone's, a manager only an active florist's in their own shop and never
   * their own (they use Profile for that, so the row records self-service
   * honestly). Couriers have no kiosk and no row here.
   */
  const canSetLanguage =
    target.isActive &&
    target.role !== "COURIER" &&
    (is("OWNER") ||
      (is("MANAGER") && target.role === "FLORIST" && viewer.id !== target.id));

  const canDeactivate =
    is("OWNER") &&
    target.isActive &&
    target.role !== "OWNER" &&
    viewer.id !== undefined &&
    viewer.id !== target.id;

  const canEditContact =
    is("OWNER") || (is("MANAGER") && target.role === "FLORIST");

  // G-105 — a manager supervises their own florists, and may look at their
  // own record. `assertCanViewTeamMemberActivity` is the matching server rule.
  const canViewActivity =
    is("OWNER") ||
    (is("MANAGER") &&
      (viewer.id === target.id || (target.role === "FLORIST" && target.isActive)));

  // G-104 — never your own: that door is Profile, and it asks for the current
  // password, which is what stops a stolen session locking you out. Never an
  // OWNER's either, mirroring `canDeactivate` (owner ruling 2026-09-22):
  // otherwise an admin or co-owner could lock the real owner out.
  const canResetPassword =
    canEditContact &&
    target.isActive &&
    accountInUse &&
    target.role !== "OWNER" &&
    viewer.id !== target.id;

  const canManageInvite =
    is("OWNER") && target.isActive && target.accountStatus !== undefined && !accountInUse;

  const canChangeEmail =
    canEditContact &&
    viewer.id !== target.id &&
    !((target.role === "OWNER" || target.role === "ADMIN") && accountInUse);

  return {
    canEditContact,
    canChangeEmail,
    canUploadContract: canEditContact,
    canResetPassword,
    canManageInvite,
    // G-126 (OD-151): owners are paid from profit, not wages — no rate,
    // commission or salary on an owner account (the server refuses them too).
    canEditPay: is("OWNER") && target.role !== "OWNER",
    canViewActivity,
    canSetLanguage,
    canSetPin,
    // Couriers and housekeeping have no home shop — the server refuses the
    // assignment, so no button (G-103, `SHOP_FREE_ROLES`).
    canReassign:
      is("OWNER") && target.role !== "OWNER" && !SHOP_FREE_ROLES.has(target.role),
    canDeactivate,
  };
}

/** True when the viewer has no action at all — the card skips its chevron. */
export function hasAnyTeamAction(p: TeamMemberPermissions): boolean {
  return (
    p.canEditContact ||
    p.canUploadContract ||
    p.canResetPassword ||
    p.canManageInvite ||
    p.canEditPay ||
    p.canViewActivity ||
    p.canSetLanguage ||
    p.canSetPin ||
    p.canReassign ||
    p.canDeactivate
  );
}
